"use client";

import {
  isPersonalFolder,
  isVirtualFolder,
  isVirtualFolderNotDefault,
} from "@zapier/utils";
import isUndefined from "lodash.isundefined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSupportedAssets } from "../folder/useSupportedAssets";
import { useFolderTreeFolder } from "../folderTree/useFolderTreeFolder";
import {
  getDisabledDeleteMessage,
  getDisabledMoveMessage,
  getDisabledShareMessage,
} from "./folderMessages";
import {
  computeFolderSubtreeMaxDepthFromHash,
  isFolderMoveBlockedByDepth,
  MAX_SUBFOLDER_DEPTH,
} from "./folderMoveDepth";
import { useAccountActionPermissions } from "./useAccountActionPermissions";
import { useCheckPermissions } from "./useCheckPermissions";
import { useUserAccountRole } from "./useUserAccountRole";
import { useFolderAssets } from "../folder/useFolderAssets";
import { useQueryFolderTree } from "../folderTree/useQueryFolderTree";

interface Input {
  folderId?: string;
  folderDepth?: number;
}
export function useFolderActionPermissions({ folderId, folderDepth }: Input) {
  const { customuserId, isAccountOwner, isSuperAdmin, isAdmin } =
    useUserAccountRole();
  const { allowTeams, isSharingEnabled, isImportExportEnabled } =
    useAccountActionPermissions();

  // If MPF is enabled, it will be enabled for the entire account
  const { isMPFEnabled } = useSupportedAssets();

  // Try to hit the permissions API as infrequently as possible
  const [permissionsCheckEnabled, setPermissionsCheckEnabled] = useState(false);
  const enablePermissionsCheck = useCallback(() => {
    if (isVirtualFolder(folderId)) {
      return; // Don't check permissions for virtual folders
    }

    setPermissionsCheckEnabled(true);
  }, [folderId]);

  const {
    checkPermissions,
    permissions,
    isLoading: arePermissionsLoading,
  } = useCheckPermissions({
    assetId: folderId,
    assetType: "folder",
    permissionLevels: ["can_edit", "can_delete"],
  });

  // Fetch permissions from the permissions API
  useEffect(() => {
    const shouldCheckPermissions =
      folderId &&
      permissionsCheckEnabled &&
      isMPFEnabled &&
      !permissions &&
      !arePermissionsLoading;

    if (shouldCheckPermissions) {
      checkPermissions();
    }
  }, [
    arePermissionsLoading,
    checkPermissions,
    folderId,
    isMPFEnabled,
    permissions,
    permissionsCheckEnabled,
  ]);

  const { folder } = useFolderTreeFolder({ folderId });
  const { folder: rootFolder } = useFolderTreeFolder({
    folderId: folder?.rootId || undefined,
  });

  const { totalCount, isLoading } = useFolderAssets({
    folderId: folderId,
    pageSize: 0,
  });

  const folderHasAssets = Boolean(!isLoading && (totalCount || 0) > 0);

  const isFolderOwner = String(folder?.ownerId) === String(customuserId);
  const isAccountOwnerOrSuperAdmin = isAccountOwner || isSuperAdmin;
  const folderHasChildren = !!folder?.children?.length;
  // If MPF is enabled, this indicates that the folder is shared with other users
  // Otherwise, it indicates that the folder is a "shared folder" that CAN be shared
  const folderIsShared = !!folder?.isShared;

  // Indicates a state where the account downgraded from a multi-user account to a single-user account
  const isFolderReadOnly = folderIsShared && !isSharingEnabled;

  // Already at top level + subtree at max depth ⇒ Move modal has no valid action.
  // Require `folder` so nested folders aren't treated as top-level while loading.
  // `== undefined` also treats API `null` parentIds as top-level.
  const isTopLevelFolder =
    !!folderId && !!folder && folder.parentId == undefined;
  const { hash: folderTreeHash } = useQueryFolderTree({
    enabled: isTopLevelFolder,
  });
  const sourceSubtreeMaxDepth = useMemo(() => {
    if (!folderId || !isTopLevelFolder) return;
    if (Object.keys(folderTreeHash).length === 0) return;
    return computeFolderSubtreeMaxDepthFromHash(folderId, folderTreeHash);
  }, [folderId, isTopLevelFolder, folderTreeHash]);
  const isMoveBlockedByDepth = isFolderMoveBlockedByDepth({
    isTopLevel: isTopLevelFolder,
    sourceSubtreeMaxDepth,
  });

  const canDelete = useMemo(() => {
    if (folderHasAssets) {
      return false;
    }
    if (isMPFEnabled) {
      return !!permissions?.canDelete;
    }
    return folderIsShared || isFolderOwner;
  }, [
    folderIsShared,
    isFolderOwner,
    isMPFEnabled,
    permissions?.canDelete,
    folderHasAssets,
  ]);

  const canMove = useMemo(() => {
    if (isVirtualFolder(folderId)) {
      return false;
    }
    if (isMoveBlockedByDepth) {
      return false;
    }
    if (isMPFEnabled) {
      return !!permissions?.canEdit;
    }
    return folderIsShared || isFolderOwner;
  }, [
    folderIsShared,
    isFolderOwner,
    isMPFEnabled,
    folderId,
    permissions?.canEdit,
    isMoveBlockedByDepth,
  ]);

  const canCreateSharedFolder = isSharingEnabled;

  const canCreateSubfolder = useMemo(() => {
    if (isVirtualFolder(folderId)) {
      return false;
    }
    if (isFolderReadOnly) {
      return false;
    }
    if (isMPFEnabled) {
      return !!permissions?.canEdit;
    }
    return folderIsShared || isFolderOwner;
  }, [
    folderIsShared,
    isFolderOwner,
    isMPFEnabled,
    isFolderReadOnly,
    folderId,
    permissions?.canEdit,
  ]);

  const canCreateZap = useMemo(() => {
    if (isPersonalFolder(folderId)) {
      return true;
    }

    if (isVirtualFolderNotDefault(folderId)) {
      return false;
    }
    if (isFolderReadOnly) {
      return false;
    }
    if (isMPFEnabled && !isVirtualFolder(folderId)) {
      return !!permissions?.canEdit;
    }
    return folderIsShared || isFolderOwner;
  }, [
    folderIsShared,
    isFolderOwner,
    isMPFEnabled,
    folderId,
    isFolderReadOnly,
    permissions?.canEdit,
  ]);

  // Rename is allowed if the folder is shared, the user is the owner,
  // or the user is an admin and the folder is not a user's default folder (id = 0)
  const canRename = useMemo(() => {
    if (isVirtualFolder(folderId)) {
      return false;
    }
    if (isMPFEnabled) {
      return !!permissions?.canEdit;
    }
    return folderIsShared || isFolderOwner || isAccountOwnerOrSuperAdmin;
  }, [
    folderIsShared,
    isFolderOwner,
    isAccountOwnerOrSuperAdmin,
    folderId,
    isMPFEnabled,
    permissions?.canEdit,
  ]);

  const canShare = useMemo(() => {
    if (isVirtualFolder(folderId)) {
      return false;
    }
    if (isMPFEnabled) {
      return !!permissions?.canEdit && !folderDepth;
    }
    return (
      (folderIsShared || isFolderOwner || isAccountOwnerOrSuperAdmin) &&
      !folderDepth &&
      !folder?.rootId
    );
  }, [
    folderIsShared,
    isFolderOwner,
    isAccountOwnerOrSuperAdmin,
    folderId,
    isMPFEnabled,
    permissions?.canEdit,
    folderDepth,
    folder,
  ]);

  const canExport = isImportExportEnabled;
  const canFilterOwners = !!isAccountOwnerOrSuperAdmin || !!isAdmin;

  const disabledShareMessage = getDisabledShareMessage({
    hasTeams: allowTeams,
    isVirtualFolder: isVirtualFolder(folderId),
    rootFolderName: rootFolder?.name || undefined,
  });

  const disabledDeleteMessage = getDisabledDeleteMessage({
    folderIsShared,
    folderHasChildren,
    folderHasAssets,
    isFolderOwner,
    ownerName: folder?.owner?.fullName,
    folderIsVirtual: isVirtualFolder(folderId),
  });

  const disabledMoveMessage = getDisabledMoveMessage({
    isBlockedByDepth: isMoveBlockedByDepth,
    maxDepth: MAX_SUBFOLDER_DEPTH,
  });

  const canCreateSubfolderLimit = isUndefined(folderDepth)
    ? false
    : folderDepth <= 4;
  const subfolderLimitMessage = "Subfolders are currently limited to 5 levels.";

  return {
    isReadOnly: isFolderReadOnly,
    canCreateSharedFolder,
    canCreateSubfolder,
    canCreateSubfolderLimit,
    canCreateZap,
    canDelete,
    canExport,
    canFilterOwners,
    canMove,
    canRename,
    canShare,
    /** FGA permissions should be checked infrequently. This indicates the need to fetch permissions. */
    enablePermissionsCheck,
    /** Indicates if permissions are currently being fetched */
    isLoading: arePermissionsLoading,
    // User messaging
    disabledDeleteMessage,
    disabledMoveMessage,
    disabledShareMessage,
    subfolderLimitMessage,
  };
}
